import { useMemo, useState } from 'react';
import { ALERT_TYPES } from 'types/billing';
import { deriveAlert, isBannerEligible } from 'utils/billing/deriveAlert';
import { usePricingConfig } from './usePricingConfig';
import { useQuota } from './useQuota';
import { useSelfServeGate } from './useSelfServeGate';

const DISMISS_STORAGE_KEY_PREFIX = 'ssb_banner_dismiss_pct';
/** Blocked-reason prefix that marks a payment-related block (drives D15 styling). */
const PAYMENT_BLOCK_REASON_PREFIX = 'payment';

export const getDismissKey = (cycleStart: string | undefined): string =>
  `${DISMISS_STORAGE_KEY_PREFIX}:${cycleStart ?? 'unknown'}`;

const isDismissed = (key: string): boolean => {
  try {
    return sessionStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};

const dismiss = (key: string): void => {
  try {
    sessionStorage.setItem(key, 'true');
  } catch {
    // sessionStorage unavailable (privacy mode / blocked storage) — dismissal is best-effort.
  }
};

export interface BillingBannerVM {
  titleKey: string;
  descriptionKey: string;
  ctaKey: string;
  value: number | string | null;
  dismissable: boolean;
  onDismiss: (() => void) | undefined;
  /** True when the block is payment-related (drives the banner's danger styling). */
  isPaymentBlocked: boolean;
  /** When true, the ConversionPopup should auto-open (D15). */
  autoOpenPopup: boolean;
  titleInterpolation: Record<string, string> | undefined;
  descriptionInterpolation: Record<string, string> | undefined;
}

/**
 * Derives the billing banner view-model for the current quota state.
 * Returns undefined when: gate is closed, no quota, alert absent, alert is trial
 * (trial states use the page hero per D13), or the banner is dismissed.
 *
 * Severity rank: blocked > overage_active > percentage_threshold (D2, §2.5).
 * Dismiss is FE-local (sessionStorage), keyed to the cycle start so it resets
 * each billing cycle.
 */
export const useBillingBanner = (): BillingBannerVM | undefined => {
  const { allowed } = useSelfServeGate();
  const { data: quota } = useQuota();
  const cfg = usePricingConfig();
  const [dismissedKey, setDismissedKey] = useState<string | undefined>(
    undefined,
  );

  return useMemo(() => {
    if (!allowed || !quota) return undefined;

    const alert = deriveAlert(quota, cfg);

    if (!alert || !isBannerEligible(alert)) return undefined;

    const cycleStart = quota.cycle?.start;
    const dismissKey = getDismissKey(cycleStart);
    const isDismissable = alert.type === ALERT_TYPES.percentageThreshold;
    const isAlreadyDismissed =
      dismissedKey === dismissKey || isDismissed(dismissKey);

    if (isDismissable && isAlreadyDismissed) return undefined;

    const onDismissHandler = isDismissable
      ? () => {
          dismiss(dismissKey);
          setDismissedKey(dismissKey);
        }
      : undefined;

    if (alert.type === ALERT_TYPES.percentageThreshold) {
      return {
        titleKey: 'billing.banner.approaching.title',
        descriptionKey: 'billing.banner.approaching.description',
        ctaKey: 'billing.banner.approaching.cta',
        value: alert.value,
        dismissable: true,
        onDismiss: onDismissHandler,
        isPaymentBlocked: false,
        autoOpenPopup: false,
        titleInterpolation: { pct: String(alert.value) },
        descriptionInterpolation: undefined,
      };
    }

    if (alert.type === ALERT_TYPES.overageActive) {
      return {
        titleKey: 'billing.banner.overage.title',
        descriptionKey: 'billing.banner.overage.description',
        ctaKey: 'billing.banner.overage.cta',
        value: alert.value,
        dismissable: false,
        onDismiss: undefined,
        isPaymentBlocked: false,
        autoOpenPopup: false,
        titleInterpolation: undefined,
        descriptionInterpolation: { credits: String(alert.value) },
      };
    }

    if (alert.type === ALERT_TYPES.blocked) {
      const reason = alert.value as string;
      const prefix = `billing.banner.blocked.${reason}`;

      return {
        titleKey: `${prefix}.title`,
        descriptionKey: `${prefix}.description`,
        ctaKey: `${prefix}.cta`,
        value: reason,
        dismissable: false,
        onDismiss: undefined,
        isPaymentBlocked: reason.startsWith(PAYMENT_BLOCK_REASON_PREFIX),
        autoOpenPopup: true,
        titleInterpolation: undefined,
        descriptionInterpolation: undefined,
      };
    }

    return undefined;
  }, [allowed, quota, cfg, dismissedKey]);
};
